import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import ApiClient from '../utils/ApiClient';
// Task 103: the assessment page can now be opened on a set that is still being analyzed, so it
// shows the same live job status the planner's set panel does.
import { fetchSplitSetStatus, jobIsActive, JOB_POLL_INTERVAL_MS } from '../utils/plannerJobs';
import {
    boardConfirmable, boardNeedsCarrierSelection, money, ModeChip, RecommendedConfirmModal,
} from './ShipmentRuns';
import { UsedConditionAckModal, usedConditionBlockFrom, buildConfirmRequestBody } from './ShipmentPlanner';

/**
 * Task 97 (owner, verbatim): "when the split functionality is working we need a way to assess all
 * the split shipments on one page".
 *
 * <p>The planner's Split-set panel answers "where does each shipment stand" (draft / analyzing /
 * analyzed / confirmed). It cannot answer "was splitting this ASIN the right call", because that
 * question is only answerable across the whole set: how many DISTINCT fulfillment centers did the
 * split actually reach, and what did that reach cost against shipping it as one. That is this
 * page.
 *
 * <p>Every action here goes through the SAME guarded endpoints the Shipment Runs board uses --
 * {@code boardConfirmable}/{@code boardNeedsCarrierSelection} gate the button, the identical
 * two-stage dialog ({@code RecommendedConfirmModal}) states exactly what will be sent, and the
 * server's task-80 used-condition guard is answered with the identical acknowledgement modal.
 * Nothing is ever confirmed automatically, and no guard is re-implemented here (a second copy of a
 * safety gate is a second chance to get it wrong).
 */

// ---------------------------------------------------------------------------
// pure helpers (exported for tests)
// ---------------------------------------------------------------------------

/**
 * Adapts a split-set member into the shape the board's gates and confirm dialog already read, so
 * both are reused VERBATIM rather than re-implemented against a second field vocabulary. The
 * single-ASIN product row is reconstructed here because a split is one ASIN by construction.
 */
export function splitMemberAsBoardRow(m) {
    if (!m) return null;
    return {
        ...m,
        totalUnits: m.units,
        probe: m.sourceMode === 'PROBE',
        products: m.asin ? [{
            asin: m.asin,
            name: m.productName,
            itemCode: m.itemCode,
            sku: m.sku,
            fnsku: m.fnsku,
            itemCondition: m.itemCondition,
            newCondition: m.newCondition,
            qty: m.units,
        }] : [],
    };
}

/** Run id of the cheapest sibling by per-unit cost. Null when fewer than two members carry a
 * comparable number -- highlighting a "winner" out of one is noise, not a finding. */
export function cheapestPerUnitRunId(members) {
    const priced = (members || []).filter(m => typeof m.perUnitCost === 'number');
    if (priced.length < 2) return null;
    return priced.reduce((best, m) => (m.perUnitCost < best.perUnitCost ? m : best)).runId;
}

/** Run id of the sibling reaching the most destination FCs; ties break on the larger 2-day
 * coverage gain. Same "needs a comparison to be worth making" rule as above. */
export function widestCoverageRunId(members) {
    const withFcs = (members || []).filter(m => (m.destinationFcs || []).length > 0);
    if (withFcs.length < 2) return null;
    const counts = new Set(withFcs.map(m => m.destinationFcs.length));
    const gains = new Set(withFcs.map(m => m.covGain2dPp ?? null));
    if (counts.size === 1 && gains.size === 1) return null; // nothing to distinguish
    return withFcs.reduce((best, m) => {
        const n = m.destinationFcs.length;
        const bn = best.destinationFcs.length;
        if (n !== bn) return n > bn ? m : best;
        return (m.covGain2dPp ?? -Infinity) > (best.covGain2dPp ?? -Infinity) ? m : best;
    }).runId;
}

/** "10.0% → 14.5%" when the run stored absolutes, else "+4.5pp", else null. Never fabricates an
 * absolute from a gain. */
export function coverageLabel(before, after, gainPp) {
    if (typeof before === 'number' && typeof after === 'number') {
        return `${before.toFixed(1)}% → ${after.toFixed(1)}%`;
    }
    if (typeof gainPp === 'number') {
        return `${gainPp >= 0 ? '+' : ''}${gainPp.toFixed(1)}pp`;
    }
    return null;
}

/**
 * Task 103: the one line this page needs about an in-progress set. A set can be opened here WHILE
 * the server is still analyzing it (that is now a normal state, not a race), and a page that
 * silently showed only the members analyzed so far would read as a finished — but wrong —
 * assessment. Returns null when there is nothing worth saying.
 */
export function splitJobBanner(job) {
    if (!job || job.phase === 'NONE') return null;
    const total = job.total || 0;
    const done = job.done || 0;
    if (jobIsActive(job)) {
        return {
            tone: 'busy',
            text: `Analyzing shipment ${Math.min(done + 1, total)} of ${total} on the server — `
                + 'this page updates as each one finishes. Nothing is confirmed automatically.',
        };
    }
    if (job.phase === 'INTERRUPTED') {
        return {
            tone: 'warn',
            text: job.error
                || 'The server restarted while this set was being analyzed — some shipments were '
                   + 'never analyzed. Nothing was confirmed; re-run the analyze from the planner.',
        };
    }
    if (job.phase === 'FAILED') {
        return { tone: 'warn', text: job.error || 'The analyze for this set failed.' };
    }
    // `failedCount`, the same name the set aggregates already use for this quantity. A finished
    // job's failures are ALSO described by the stored-run banner below (SplitSetAggregates
    // .failedCount), so this one only speaks when the job itself has something extra to say.
    const failed = job.failedCount || job.result?.failedCount || 0;
    if (failed > 0) {
        return { tone: 'warn', text: job.result?.summary || `${failed} shipment(s) failed to analyze.` };
    }
    return null;
}

export function rollupPillClass(status) {
    if (status === 'CONFIRMED') return 'bg-blue-100 text-blue-800';
    if (status === 'PARTIALLY_CONFIRMED') return 'bg-amber-100 text-amber-800';
    if (status === 'OPEN') return 'bg-green-100 text-green-800';
    return 'bg-gray-200 text-gray-600';
}

export function statusPillClass(status) {
    const s = String(status || '').toUpperCase();
    if (s === 'ACTIVE') return 'bg-green-100 text-green-800';
    if (s === 'CONFIRMED') return 'bg-blue-100 text-blue-800';
    if (s === 'CANCELLED' || s === 'SUPERSEDED') return 'bg-gray-200 text-gray-600';
    return 'bg-gray-100 text-gray-600';
}

export function decisionPillClass(decision) {
    if (decision === 'TAKE') return 'bg-green-100 text-green-800';
    if (decision === 'CONSIDER') return 'bg-amber-100 text-amber-800';
    if (decision === 'SKIP') return 'bg-gray-200 text-gray-600';
    return 'bg-gray-100 text-gray-500';
}

/** The one sentence the header exists for: did splitting reach further, and what did it cost. */
export function baselineSentence(baseline, aggregates) {
    if (!baseline) return null;
    const parts = [];
    if (typeof baseline.totalCostDelta === 'number') {
        const more = baseline.totalCostDelta >= 0;
        parts.push(`${more ? 'costs' : 'saves'} ${money(Math.abs(baseline.totalCostDelta))}`
            + (typeof baseline.perUnitDelta === 'number'
                ? ` (${money(Math.abs(baseline.perUnitDelta))}/unit)` : ''));
    }
    if (typeof baseline.fcCountDelta === 'number' && baseline.fcCountDelta !== 0) {
        const more = baseline.fcCountDelta > 0;
        parts.push(`reaches ${Math.abs(baseline.fcCountDelta)} ${more ? 'more' : 'fewer'} `
            + `FC${Math.abs(baseline.fcCountDelta) === 1 ? '' : 's'}`);
    }
    if (parts.length === 0) return null;
    const reach = aggregates?.distinctFcCount;
    return `Split into ${aggregates?.shipments} shipments ${parts.join(' and ')} `
        + `than the ${baseline.totalUnits}-unit single shipment `
        + `(run #${baseline.runId})${reach ? ` — ${reach} FCs reached` : ''}.`;
}

// ---------------------------------------------------------------------------
// presentation
// ---------------------------------------------------------------------------

function StatCard({ label, value, sub, emphasis }) {
    return (
        <div className={`rounded-lg border px-3 py-2 ${emphasis
            ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-white'}`}>
            <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
            <div className={`text-lg font-semibold ${emphasis ? 'text-indigo-900' : 'text-gray-900'}`}>
                {value}
            </div>
            {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
        </div>
    );
}

export function SplitSetHeader({ set }) {
    const a = set.aggregates || {};
    const sentence = baselineSentence(set.baseline, a);
    return (
        <div data-testid="split-set-header">
            <div className="flex items-baseline gap-3 flex-wrap">
                <h1 className="text-xl font-semibold text-gray-900">
                    {set.productName || set.asin || set.groupId}
                </h1>
                <span className="font-mono text-xs text-gray-500">
                    {set.itemCode ? `${set.itemCode} · ` : ''}{set.asin || '—'}
                </span>
                <span className="text-xs text-gray-400 font-mono">{set.groupId}</span>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
                {set.members.length} of {set.splitTotal || set.members.length} shipments analyzed.
                Each one is confirmed separately — nothing on this page confirms anything on its own.
            </p>

            {/* The set-level truth about a failed analyze. Everything in the stat cards below --
                distinct FCs, freight, blended per unit -- is computed only from the members Amazon
                actually answered for, so when some failed the numbers describe a smaller split than
                the operator made. Said once, at the top, rather than left to be deduced from empty
                cells. (2026-08-21: an entire 3-way split came back like this and read as "0 FCs
                reached, every option SKIP".) */}
            {a.failedCount > 0 && (
                <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
                    data-testid="split-set-failed-banner">
                    <span className="font-semibold">
                        {a.failedCount} of {a.shipments} shipment{a.shipments === 1 ? '' : 's'} failed to analyze.
                    </span>{' '}
                    {a.failedCount === a.shipments
                        ? 'Nothing in this set was quoted — the figures below are placeholders, not results. '
                        : 'The figures below cover only the shipments Amazon answered for, so treat them as a lower bound. '}
                    The inbound plans still exist at Amazon and can be cancelled. Hover a row&apos;s red
                    badge for the reason, then re-analyze that shipment.
                </div>
            )}

            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                <StatCard label="Shipments" value={a.shipments ?? 0} />
                <StatCard label="Units" value={a.totalUnits ?? 0} />
                <StatCard
                    label="Distinct FCs"
                    value={a.distinctFcCount ?? 0}
                    emphasis
                    sub={a.overlappingFcCount > 0
                        ? `${a.overlappingFcCount} overlapping — that much of the split did not diversify`
                        : 'no overlap between shipments'}
                />
                <StatCard
                    label="Boxes"
                    value={a.totalBoxes == null ? '—' : a.totalBoxes}
                    sub={a.totalBoxes != null && !a.boxesFullyKnown ? 'partial — some ASINs unpackaged' : null}
                />
                <StatCard
                    label="Freight + fees"
                    value={a.totalCost == null ? '—' : money(a.totalCost)}
                    sub={a.freightFullyKnown
                        ? `${money(a.totalFreight)} freight + ${money(a.totalPlacementFees)} fees`
                        : `${a.freightUnknownShipments} shipment(s) unquoted — total withheld`}
                />
                <StatCard
                    label="Blended / unit"
                    value={a.blendedPerUnit == null ? '—' : money(a.blendedPerUnit)}
                />
            </div>

            {a.distinctFcs?.length > 0 && (
                <p className="mt-2 text-xs text-gray-600">
                    <span className="text-gray-400">Reaches: </span>
                    <span className="font-mono">{a.distinctFcs.join(', ')}</span>
                </p>
            )}

            {sentence && (
                <p className="mt-2 text-sm text-indigo-900 bg-indigo-50 border border-indigo-200 rounded px-3 py-2"
                    data-testid="split-set-baseline">
                    {sentence}
                </p>
            )}
            {!set.baseline && (
                <p className="mt-2 text-xs text-gray-400">
                    No comparable single-shipment run for these units — no baseline to compare against.
                </p>
            )}
        </div>
    );
}

function MemberRow({ member, cheapestRunId, widestRunId, onOpen, onConfirm, onCancel, cancelling }) {
    const row = splitMemberAsBoardRow(member);
    const confirmable = boardConfirmable(row);
    const needsCarrier = boardNeedsCarrierSelection(row);
    const dimmed = !confirmable;
    const coverage = coverageLabel(member.covBefore2dPct, member.covAfter2dPct, member.covGain2dPp);
    return (
        <tr className={dimmed ? 'bg-gray-50 text-gray-400' : 'hover:bg-emerald-50'}
            data-testid={`split-set-member-${member.splitIndex ?? member.runId}`}>
            <td className="px-3 py-2">
                <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-xs font-semibold whitespace-nowrap">
                    {member.splitIndex ?? '?'}/{member.splitTotal ?? '?'}
                </span>
                <div className="text-[11px] text-gray-400 mt-1 font-mono">#{member.runId}</div>
            </td>
            <td className="px-3 py-2 text-sm">
                {member.units}
                <span className="text-xs text-gray-400">
                    {' '}units{member.boxes != null ? ` · ${member.boxes} box${member.boxes === 1 ? '' : 'es'}` : ''}
                </span>
            </td>
            <td className="px-3 py-2 text-xs font-mono">
                {(member.destinationFcs || []).join(', ') || '—'}
                {member.runId === widestRunId && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-[10px] font-semibold">
                        widest reach
                    </span>
                )}
            </td>
            <td className="px-3 py-2"><ModeChip mode={member.mode} carrier={member.carrier} /></td>
            <td className="px-3 py-2 text-sm whitespace-nowrap">
                {member.freightKnown ? money(member.freight) : <span className="text-amber-700 text-xs">not quoted</span>}
                {member.placementFee != null && (
                    <span className="text-[11px] text-gray-400"> + {money(member.placementFee)} fee</span>
                )}
            </td>
            <td className="px-3 py-2 text-sm whitespace-nowrap">
                {money(member.perUnitCost)}
                {member.runId === cheapestRunId && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold">
                        cheapest
                    </span>
                )}
            </td>
            <td className="px-3 py-2">
                {/* A member whose analysis failed has no verdict to report. It used to land here as
                    a grey "SKIP" pill built from placeholder zeros -- the operator read a decision
                    where Amazon had never answered the question. */}
                {member.analysisError ? (
                    <span
                        className="px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap bg-red-100 text-red-800 cursor-help"
                        title={member.analysisError}
                        data-testid={`split-set-member-failed-${member.splitIndex ?? member.runId}`}
                    >
                        analysis failed<span className="ml-1 opacity-70">ⓘ</span>
                    </span>
                ) : (
                <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${decisionPillClass(member.decision)} ${member.decisionReason ? 'cursor-help' : ''}`}
                    title={member.decisionReason || undefined}
                >
                    {member.decision || 'no verdict'}
                    {member.decisionReason && <span className="ml-1 opacity-70">ⓘ</span>}
                </span>
                )}
                {typeof member.drawPercentile === 'number' && (
                    <div className="text-[11px] text-gray-400 mt-0.5">
                        draw p{Math.round(member.drawPercentile)}
                    </div>
                )}
            </td>
            <td className="px-3 py-2 text-xs">{coverage || '—'}</td>
            <td className="px-3 py-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusPillClass(member.status)}`}>
                    {member.status || '—'}
                </span>
                {member.confirmedShipmentIds?.length > 0 && (
                    <div className="text-[11px] text-gray-500 mt-0.5 font-mono">
                        {member.confirmedShipmentIds.join(', ')}
                    </div>
                )}
                {!confirmable && member.notActionableReason && (
                    <div className="text-[11px] text-amber-700 mt-0.5 max-w-[11rem]">{member.notActionableReason}</div>
                )}
            </td>
            <td className="px-3 py-2 whitespace-nowrap">
                <button onClick={() => onOpen(member)}
                    className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline">
                    Open
                </button>
                {confirmable && (needsCarrier ? (
                    <span className="ml-2 text-xs text-gray-400 cursor-not-allowed"
                        title="This plan needs a carrier choice — open it to choose a carrier">
                        Confirm
                    </span>
                ) : (
                    <button onClick={() => onConfirm(member)}
                        className="ml-2 text-xs text-emerald-600 hover:text-emerald-800 hover:underline">
                        Confirm
                    </button>
                ))}
                {member.planId && String(member.status).toUpperCase() === 'ACTIVE' && (
                    <button onClick={() => onCancel(member)}
                        disabled={cancelling === member.planId}
                        className="ml-2 text-xs text-red-500 hover:text-red-700 hover:underline disabled:opacity-50">
                        {cancelling === member.planId ? 'Cancelling…' : 'Cancel'}
                    </button>
                )}
            </td>
        </tr>
    );
}

export function SplitSetTable({ members, onOpen, onConfirm, onCancel, cancelling }) {
    // Sorted by split index -- the order the operator created them. Sorting by cost would hide
    // which shipment is which, and the whole page is a comparison of siblings by position.
    const sorted = useMemo(
        () => [...(members || [])].sort((a, b) => (a.splitIndex ?? 99) - (b.splitIndex ?? 99)),
        [members]);
    const cheapestRunId = useMemo(() => cheapestPerUnitRunId(sorted), [sorted]);
    const widestRunId = useMemo(() => widestCoverageRunId(sorted), [sorted]);
    return (
        <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
            <table className="min-w-full text-sm" data-testid="split-set-table">
                <thead className="bg-gray-50 text-xs text-gray-500">
                    <tr>
                        <th className="text-left font-medium px-3 py-2">#</th>
                        <th className="text-left font-medium px-3 py-2">Contents</th>
                        <th className="text-left font-medium px-3 py-2">Destinations</th>
                        <th className="text-left font-medium px-3 py-2">Mode</th>
                        <th className="text-left font-medium px-3 py-2">Freight</th>
                        <th className="text-left font-medium px-3 py-2">Per unit</th>
                        <th className="text-left font-medium px-3 py-2">Decision</th>
                        <th className="text-left font-medium px-3 py-2">2-day coverage</th>
                        <th className="text-left font-medium px-3 py-2">Status</th>
                        <th className="text-left font-medium px-3 py-2">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {sorted.map((m) => (
                        <MemberRow
                            key={m.runId}
                            member={m}
                            cheapestRunId={cheapestRunId}
                            widestRunId={widestRunId}
                            onOpen={onOpen}
                            onConfirm={onConfirm}
                            onCancel={onCancel}
                            cancelling={cancelling}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function SplitSetIndex({ sets, loading }) {
    if (loading) return <div className="text-sm text-gray-500">Loading split sets…</div>;
    if (!sets || sets.length === 0) {
        return (
            <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg p-6 text-center">
                No split sets yet. Break an ASIN into multiple shipments on the Shipment Planner and
                analyze them — the set will appear here.
            </div>
        );
    }
    return (
        <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
            <table className="min-w-full text-sm" data-testid="split-set-index">
                <thead className="bg-gray-50 text-xs text-gray-500">
                    <tr>
                        <th className="text-left font-medium px-3 py-2">Product</th>
                        <th className="text-left font-medium px-3 py-2">Shipments</th>
                        <th className="text-left font-medium px-3 py-2">Units</th>
                        <th className="text-left font-medium px-3 py-2">Created</th>
                        <th className="text-left font-medium px-3 py-2">Status</th>
                        <th className="text-left font-medium px-3 py-2"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {sets.map((s) => (
                        <tr key={s.groupId} className="hover:bg-indigo-50">
                            <td className="px-3 py-2">
                                <div className="text-gray-800">{s.productName || s.asin || s.groupId}</div>
                                <div className="text-[11px] font-mono text-gray-400">
                                    {s.itemCode ? `${s.itemCode} · ` : ''}{s.asin || '—'}
                                </div>
                            </td>
                            <td className="px-3 py-2">
                                {s.shipments}{s.splitTotal && s.splitTotal !== s.shipments ? ` of ${s.splitTotal}` : ''}
                            </td>
                            <td className="px-3 py-2">{s.totalUnits}</td>
                            <td className="px-3 py-2 text-xs text-gray-500">
                                {s.createdAt ? new Date(s.createdAt).toLocaleString() : '—'}
                            </td>
                            <td className="px-3 py-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${rollupPillClass(s.rollupStatus)}`}>
                                    {String(s.rollupStatus || '').replace('_', ' ').toLowerCase() || '—'}
                                </span>
                            </td>
                            <td className="px-3 py-2">
                                <Link to={`/split-sets/${encodeURIComponent(s.groupId)}`}
                                    className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline">
                                    Assess →
                                </Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ---------------------------------------------------------------------------
// page
// ---------------------------------------------------------------------------

export default function SplitSets() {
    const { groupId } = useParams();
    const navigate = useNavigate();

    const [sets, setSets] = useState([]);
    const [set, setSet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [confirmTarget, setConfirmTarget] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [usedAckItems, setUsedAckItems] = useState(null);
    const [usedAckRetry, setUsedAckRetry] = useState(null);
    const [cancelling, setCancelling] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (groupId) {
                const res = await ApiClient.get(
                    `/api/shipment-planner/split-sets/${encodeURIComponent(groupId)}`);
                setSet(res.data || null);
            } else {
                const res = await ApiClient.get('/api/shipment-planner/split-sets');
                setSets(res.data || []);
            }
        } catch (err) {
            setError(err?.response?.status === 404
                ? 'That split set no longer exists.'
                : (err?.response?.data?.error || err.message || 'Failed to load split sets'));
        } finally {
            setLoading(false);
        }
    }, [groupId]);

    useEffect(() => { load(); }, [load]);

    /**
     * Task 103 -- live status for a set that is still being analyzed. Polls the group's job while
     * it is active and reloads the assessment each time another member lands, so an operator who
     * kicked off "analyze all" in the planner and navigated straight here watches the table fill
     * in instead of seeing a half-set that looks complete.
     *
     * Read-only, like everything else on this page: it observes the job, it cannot start one.
     */
    const [job, setJob] = useState(null);
    useEffect(() => {
        if (!groupId) return undefined;
        let cancelled = false;
        let timer = null;
        let lastDone = -1;
        const tick = async () => {
            const next = await fetchSplitSetStatus(groupId);
            if (cancelled) return;
            setJob(next);
            // Reload the assessment only when the server has actually finished another member --
            // polling the heavy detail endpoint every second would be wasteful and would flicker.
            if (next && (next.done || 0) !== lastDone) {
                lastDone = next.done || 0;
                if (lastDone > 0) load();
            }
            if (jobIsActive(next)) timer = setTimeout(tick, JOB_POLL_INTERVAL_MS);
        };
        tick();
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId]);
    const banner = splitJobBanner(job);

    const openMember = (m) => navigate(`/shipment-planner?run=${m.runId}`);

    /**
     * The board's confirm, unchanged: gates re-checked, body built by the shared
     * buildConfirmRequestBody, and the server's used-condition refusal answered by the shared
     * acknowledgement modal rather than pre-acknowledged here. `acknowledgeUsedCondition` is only
     * ever true on a retry the operator explicitly ticked.
     */
    const doConfirm = async (member, acknowledgeUsedCondition = false) => {
        const row = splitMemberAsBoardRow(member);
        if (!row || !boardConfirmable(row) || boardNeedsCarrierSelection(row)) return;
        setConfirming(true);
        try {
            const res = await ApiClient.post('/api/shipment-planner/confirm', buildConfirmRequestBody({
                planId: row.planId,
                placementOptionId: row.placementOptionId,
                acknowledgeUsedCondition,
            }), { timeout: 120000 });
            const count = res.data?.shipmentCount;
            toast.success(`Confirmed — shipment ${member.splitIndex ?? '?'} of `
                + `${member.splitTotal ?? '?'} · ${count ?? 0} shipment(s) sent to Amazon.`);
            setConfirmTarget(null);
            setUsedAckItems(null);
            setUsedAckRetry(null);
            await load();
        } catch (err) {
            const blocked = usedConditionBlockFrom(err);
            if (blocked) {
                setUsedAckItems(blocked);
                setUsedAckRetry(() => () => doConfirm(member, true));
                setConfirming(false);
                return;
            }
            toast.error(err?.response?.data?.error || err.message || 'Confirm failed');
        } finally {
            setConfirming(false);
        }
    };

    const doCancel = async (member) => {
        if (!member?.planId) return;
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Cancel shipment ${member.splitIndex ?? '?'} of this split `
            + `(Run #${member.runId}) on Amazon?`)) return;
        setCancelling(member.planId);
        try {
            await ApiClient.post(`/api/shipment-planner/cancel/${member.planId}`);
            toast.success(`Cancelled Run #${member.runId}.`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.error || err.message || 'Cancel failed');
        } finally {
            setCancelling(null);
        }
    };

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
                {groupId && (
                    <Link to="/split-sets" className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline">
                        ← All split sets
                    </Link>
                )}
                {!groupId && <h1 className="text-xl font-semibold text-gray-900">Split Sets</h1>}
            </div>

            {!groupId && (
                <p className="text-sm text-gray-500">
                    Every ASIN that was broken into multiple shipments. Open one to compare its
                    shipments side by side.
                </p>
            )}

            {/* Task 103: while the server is still analyzing the set's FIRST member there are no
                runs to assess yet, so the detail endpoint honestly 404s. Showing "that split set
                no longer exists" there would be a lie -- the banner below says what is actually
                happening, so the error is suppressed for exactly that window. */}
            {error && !jobIsActive(job) && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {error}
                </div>
            )}

            {banner && (
                <div
                    className={`text-sm rounded px-3 py-2 border ${banner.tone === 'busy'
                        ? 'text-indigo-900 bg-indigo-50 border-indigo-200'
                        : 'text-amber-900 bg-amber-50 border-amber-200'}`}
                    data-testid="split-set-job-banner"
                >
                    {banner.text}
                </div>
            )}

            {!groupId && !error && <SplitSetIndex sets={sets} loading={loading} />}

            {groupId && loading && <div className="text-sm text-gray-500">Loading split set…</div>}

            {groupId && !loading && set && (
                <>
                    <SplitSetHeader set={set} />
                    <SplitSetTable
                        members={set.members}
                        onOpen={openMember}
                        onConfirm={setConfirmTarget}
                        onCancel={doCancel}
                        cancelling={cancelling}
                    />
                </>
            )}

            <RecommendedConfirmModal
                open={!!confirmTarget}
                row={splitMemberAsBoardRow(confirmTarget)}
                onCancel={() => setConfirmTarget(null)}
                onConfirm={() => doConfirm(confirmTarget)}
                busy={confirming}
            />
            <UsedConditionAckModal
                open={!!usedAckItems}
                items={usedAckItems || []}
                busy={confirming}
                onCancel={() => { setUsedAckItems(null); setUsedAckRetry(null); }}
                onAcknowledge={() => usedAckRetry && usedAckRetry()}
            />
        </div>
    );
}
